import React from "react";
import SyncingDataGraphic from "../../../../../assets/img/syncing-data.png";
import { DOCS_WEBHOOKS_PATH } from "../../../../../router/RouterUtils";
import { Alert, Card, Link, Text } from "@merge-api/merge-javascript-shared";

// Create space between list item markers and content - was too tight
const SyncingData = () => {
  return (
    <Card className="p-6 mx-auto w-full lg:w-3/4">
      <Text variant="h3" className="mb-1">
        Syncing data
      </Text>
      <Text as="p" className="mb-6 text-gray-40">
        Sync user data effectively with Merge
      </Text>

      <p className="mt-6">There are a few methods involved in syncing data with Merge:</p>
      <ol className="mt-6">
        <li className="relative left-2 mb-3">Polling</li>
        <li className="relative left-2">Webhooks</li>
      </ol>
      <div className="flex justify-center">
        <img
          src={SyncingDataGraphic}
          alt="Syncing Merge data"
          className="w-full max-w-[600px] my-2 mx-auto mb-6"
        />
      </div>
      <p className="my-6">Merge recommends syncing data in the following way:</p>
      <ol>
        <li className="relative left-2 mb-9">
          <p>
            <b>Create a set of functions in your backend that are responsible for syncing data</b>
          </p>
          <p>
            These functions should utilize the <code>modified_after</code> timestamp filter that is
            available on all Merge list endpoints.
          </p>

          <p>
            This critical filter enables you to only pull data that has been changed (or created)
            since your last sync.
          </p>

          <p>
            For example, you can ask for <code>modified_after=2021-03-30T20:44:18.662942Z</code>,
            and only pull items that are new / different.
          </p>
          <p className="mb-6">
            To do this, you need to store the time you last began a fetch to Merge&apos;s API for
            the linked account and endpoint.
          </p>

          <Alert title="Important:" color="gray">
            Use the time the fetch began since data can change during a fetch. Pick up those changes
            during the next sync.
          </Alert>
        </li>

        <li className="relative left-2 mb-9">
          <p>
            <b>Sync periodically (aka polling)</b>
          </p>
          <p>
            Configure your backend to call your sync functions periodically, such as every 24 hours.
          </p>
        </li>

        <li className="relative left-2 mb-9">
          <p>
            <b>Sync when Merge emits a "Sync Notification" webhook</b>
          </p>

          <p>Merge offers a variety of webhooks for real-time events.</p>

          <p>
            To be alerted to when Merge may have data updates, configure Merge to notify you via
            webhook when Merge has completed syncing with a third-party platform for a linked
            account.
          </p>

          <p>
            Whenever you receive a <b>"Sync Notification" webhook</b>, initiate the logic you
            configured in Step 1.
          </p>

          <p>
            We recommend using a combination of our &quot;First Sync Notification&quot; and
            &quot;Any Sync Notification&quot; webhooks to help you manage your sync activities at
            scale.
          </p>

          <p>
            Learn how to configure Merge webhooks in our{" "}
            <Link href={DOCS_WEBHOOKS_PATH} target="_blank">
              webhook docs
            </Link>
            .
          </p>

          <Alert
            className="my-6"
            color="gray"
            title='I see Merge offers "Changed Data" webhooks. Can I use those?'
          >
            Merge offers webhooks that deliver updated data when individual data instances are
            updated, but depending on the amount of user data, this will not scale well.
          </Alert>

          <Alert
            color="gray"
            title="Make sure to implement polling and don't rely entirely on notification webhooks."
          >
            <div className="flex flex-col">
              <Text as="p">
                They can fail for a variety of reasons (such as downtime on your end or failed
                processing).
              </Text>

              <Text>
                Merge does attempt to redeliver multiple times using exponential backoff, but we
                still recommend calling your sync functions on a periodic cadence of around once
                every 24 hours.
              </Text>
            </div>
          </Alert>
        </li>
      </ol>
    </Card>
  );
};

export default SyncingData;
